import { lazy, Suspense, useEffect, useMemo } from 'react';
import Modal from 'react-modal';
import { Switch, withRouter } from 'react-router';
import { Route, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import AppConfiguration from './components/AppConfiguration/AppConfiguration';
import { CartContainer } from './components/Cart';
import FloatingMessage from './components/FloatingMessage/FloatingMessage';
import IEModal from './components/IEModal';
import { FLOW_NAMES } from './flows/DynamicFlow/utilities/constants';
import { getDevFlowNameFromPath } from './flows/DynamicFlow/utilities/helpers.js';
import GlobalStyle from './GlobalStyle';
import routes from './routes';
import withAppInsights from './utilities/applicationInsights';
import { checkCookie, getCookie, setCookie } from './utilities/cookiesUtils';
import { isDevEnvironment } from './utilities/env';
import isAuthorMode from './utilities/isAuthorMode';

const DynamicFlow = lazy(() => import(/* webpackChunkName: "DynamicFlow" */ './flows/DynamicFlow/DynamicFlow'));

const CartHandoffGate = lazy(() =>
  import(/* webpackChunkName: "CartHandoffGate" */ './components/CartHandoff/CartHandoffGate')
);
const BookTab = lazy(() => import(/* webpackChunkName: "BookTab" */ './components/BookTab/index'));
const EmailCapture = lazy(() => import(/* webpackChunkName: "EmailCapture" */ './components/EmailCapture'));
const AppLayout = lazy(() => import(/* webpackChunkName: "AppLayout" */ './layouts/AppLayout'));
const SummaryLayout = lazy(() => import(/* webpackChunkName: "SummaryLayout" */ './layouts/SummaryLayout'));
const NoMatchLayout = lazy(() => import(/* webpackChunkName: "NoMatchLayout" */ './layouts/NoMatchLayout'));

const NavigationMenuLayout = lazy(() =>
  import(
    /* webpackChunkName: "NavigationMenuLayout" */
    './layouts/NavigationMenuLayout'
  )
);
const PaymentForm = lazy(() => import(/* webpackChunkName: "PaymentForm" */ './components/PaymentForm'));
const GlobalModal = lazy(() => import(/* webpackChunkName: "GlobalModal" */ './components/GlobalModal'));
const PlanSearchForm = lazy(() => import(/* webpackChunkName: "PlanSearchForm" */ './components/PlanSearchForm'));
const BirthdaySearchFormRouter = lazy(() =>
  import(/* webpackChunkName: "BirthdaySearchFormRouter" */ './components/BirthdaySearchForm/BirthdaySearchFormRouter')
);
const PreCheckIn = lazy(() => import(/* webpackChunkName: "Reservation" */ './pages/PreCheckIn'));
const Reservations = lazy(() => import(/* webpackChunkName: "Reservations" */ './pages/Reservations'));
const MyReservations = lazy(() =>
  import(/* webpackChunkName: "MyReservations" */ './pages/Reservations/MyReservations')
);
const ReferAFriendPage = lazy(() => import(/* webpackChunkName: "ReferAFriend" */ './pages/User/ReferAFriendPage'));
const UserProfile = lazy(() => import(/* webpackChunkName: "UserProfile" */ './pages/User/Profile'));
const MyRecentSearches = lazy(() =>
  import(/* webpackChunkName: "MyRecentSearches" */ './pages/RecentSearches/MyRecentSearches')
);
const ChangePassword = lazy(() => import(/* webpackChunkName: "ChangePassword" */ './pages/ChangePassword'));
const UpdatePassword = lazy(() =>
  import(/* webpackChunkName: "UpdatePassword" */ './pages/ChangePassword/UpdatePassword')
);
const UnlockAccount = lazy(() => import(/* webpackChunkName: "UnlockAccount" */ './pages/UnlockAccount'));
const ReservationDetail = lazy(() => import(/* webpackChunkName: "ReservationDetail" */ './pages/ReservationDetail'));
const MyReservationsPage = lazy(() => import(/* webpackChunkName: "MyReservationsPage" */ './pages/MyReservations'));
const MyPreferences = lazy(() => import(/* webpackChunkName: "MyPreferences" */ './pages/User/Preferences'));

const DaypassBooking = lazy(() => import(/* webpackChunkName: "DaypassBooking" */ './pages/Daypass/DaypassBooking'));
const EmailQueuePage = lazy(() => import(/* webpackChunkName: "LandingPageForm" */ './pages/EmailQueue'));
const MysteryModal = lazy(() => import(/* webpackChunkName: "MysteryModal" */ './components/MysteryModal'));
const Folio = lazy(() => import(/* webpackChunkName: "Folio" */ './pages/Folio'));
const IframeHandler = lazy(() => import(/* webpackChunkName: "IframeHandler" */ './pages/IframeHandler'));
const VoyagersClub = lazy(() => import(/* webpackChunkName: "VoyagersClub" */ './pages/VoyagersClub'));
const Seasonpass = lazy(() => import(/* webpackChunkName: "Seasonpass" */ './pages/Seasonpass'));
const SeasonpassRedeem = lazy(() => import(/* webpackChunkName: "SeasonpassRedeem" */ './pages/Seasonpass/Redeem'));

Modal.setAppElement(document.getElementById('root'));

const App = ({ history, flowName }) => {
  const location = useLocation();

  if (isDevEnvironment) {
    flowName = getDevFlowNameFromPath(location.pathname);
    console.log('DEBUG = flowname is (from getDevFlowNameFromPath) -> ', flowName);
  }
  if (!flowName) {
    console.warn('No flowName attribute found in HTML');
  }

  const flowAudience = window.flowAudience ?? 'ALL';

  // USE THE FOLLOWING LINE TO ACTIVATE THE PERSONALIZATION FLOW:
  // const flowAudience = window.flowAudience ?? 'personalization';

  history.listen(() => {
    // It'll be used by the AOS library being used in the AEM project
    // that needs to trigger the footer animation when we're switching
    // between React routes
    window.dispatchEvent(new Event('resize'));
  });
  const { search } = location;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  useEffect(() => {
    //This if statement to setup the did cookie
    //==========================================
    if (!checkCookie('did')) {
      setCookie('did', uuidv4(), 3650);
    }
    //This if statement is to setup the eid cookie
    //=============================================
    if (searchParams.get('eid') !== null) {
      //If the eid query string exists and the eid cookie doesn't exits create one
      //===========================================================================
      if (!checkCookie('eid')) setCookie('eid', searchParams.get('eid'), 3650);
      //If eid cookie exists but is different from the query string update the cookie with the query string
      //====================================================================================================
      if (
        getCookie('eid') !== null &&
        searchParams.get('eid') !== null &&
        searchParams.get('eid') !== getCookie('eid')
      ) {
        setCookie('eid', searchParams.get('eid'), 3650);
      }
    }
    //This if statement is to setup the pastGuest cookie
    //===================================================
    if (searchParams.get('pg') !== null) {
      //If the pg query string exists and the pastGuest cookie doesn't exits create one
      //==================================================================================
      if (!checkCookie('pastGuest')) setCookie('pastGuest', searchParams.get('pg'), 3650);
      //If pastGuest cookie exists but has different value from the pg from the query string it will update the cookie
      //===============================================================================================================
      if (
        getCookie('pastGuest') !== null &&
        searchParams.get('pg') !== null &&
        searchParams.get('pg') !== getCookie('pastGuest')
      ) {
        setCookie('pastGuest', searchParams.get('pg'), 3650);
      }
    }
  }, [history.location.pathname, searchParams]);

  return (
    <AppConfiguration>
      <div className="booking-engine-root-container">
        <GlobalStyle />
        <IEModal />
        <Suspense fallback={<div></div>}>
          {searchParams.get('mystery') === 'true' ? <MysteryModal /> : null}
          <GlobalModal />
          <CartContainer flowName={flowName} />
          <FloatingMessage />
          {!isAuthorMode() && <EmailCapture />}
          {/* Can remove check for !== FLOW_NAMES.DAYPASS once Daypass flow is part of DynamicFlow */}
          {flowName && flowName !== FLOW_NAMES.DAYPASS ? (
            <DynamicFlow markupFlowAudience={flowAudience} markupFlowName={flowName} />
          ) : (
            <>
              <Route
                path={[
                  routes.blank,
                  routes.reservations.self.toString(),
                  routes.myRecentSearches,
                  routes.profile.toString()
                ]}
                exact
                component={BookTab}
              />
              <Switch>
                <Route exact path={routes.cart} component={CartHandoffGate} />
                <AppLayout exact path={routes.widget} shouldUnWrapFromContainer component={IframeHandler} />
                <AppLayout
                  exact
                  path={[routes.blank, routes.home]}
                  shouldUnWrapFromContainer
                  component={matchProps => {
                    return (
                      <PlanSearchForm
                        shouldHideOnMobile
                        shouldOpenCalendarUp
                        shouldRemoveBorderRadius
                        {...matchProps}
                      />
                    );
                  }}
                />
                <AppLayout
                  exact
                  path={routes.birthdayParties.self}
                  shouldUnWrapFromContainer
                  component={matchProps => {
                    return <BirthdaySearchFormRouter {...matchProps} />;
                  }}
                />
                <AppLayout exact path={routes.seasonpass.booking} component={Seasonpass} />

                <AppLayout exact path={routes.promoSignUp} component={EmailQueuePage} />
                <AppLayout exact path={routes.reservation.detail.folio} component={Folio} />
                {/* The below route will eventually be part of daypass flow, and will be removed from here */}
                <AppLayout exact path={routes.daypass.booking} component={DaypassBooking} />
                <AppLayout exact path={routes.seasonpass.redeem} component={SeasonpassRedeem} />
                {/* The below route will eventually be part of daypass flow and will be removed from here */}
                <SummaryLayout exact path={routes.daypass.payment} component={PaymentForm} />
                <SummaryLayout exact path={routes.seasonpass.payment} component={PaymentForm} />
                <AppLayout
                  exact
                  shouldUnWrapFromContainer
                  path={[routes.plan.paymentSuccess, routes.daypass.paymentSuccess, routes.seasonpass.paymentSuccess]}
                  component={ReservationDetail}
                />
                <AppLayout exact path={routes.voyagersClub} component={VoyagersClub} />
                <NavigationMenuLayout exact path={routes.profile.self} component={UserProfile} />
                <NavigationMenuLayout exact path={routes.referAFriend} component={ReferAFriendPage} />
                <AppLayout exact path={routes.reservations.self} component={Reservations} />
                <NavigationMenuLayout exact path={routes.reservations.myReservations.self} component={MyReservations} />
                <NavigationMenuLayout exact path={routes.profile.myPreferences} component={MyPreferences} />
                <AppLayout
                  exact
                  shouldUnWrapFromContainer
                  path={[
                    routes.reservations.myReservations.detail.self,
                    routes.reservations.myReservations.detail.packagemodal
                  ]}
                  component={MyReservationsPage}
                />
                <AppLayout
                  shouldUnWrapFromContainer
                  exact
                  path={routes.reservation.detail.checkIn}
                  component={PreCheckIn}
                />
                <AppLayout path={routes.forgotPassword} component={ChangePassword} exact />
                <AppLayout path={routes.changePassword} component={UpdatePassword} exact />
                <AppLayout path={routes.unlockAccount} component={UnlockAccount} exact />
                <NavigationMenuLayout exact path={routes.myRecentSearches} component={MyRecentSearches} />
                <AppLayout
                  shouldUnWrapFromContainer
                  exact
                  path={[routes.planSearchForm.t2, routes.planSearchForm.t3]}
                  component={matchProps => {
                    return (
                      <>
                        <BookTab {...matchProps} />
                        <PlanSearchForm shouldHideOnMobile {...matchProps} />
                      </>
                    );
                  }}
                />
                <AppLayout exact path={[routes.planSearchForm]} component={PlanSearchForm} />
                {/* This layout is the catch-all for non-Plan components that still renders dynamic features on AEM */}
                <NoMatchLayout />
              </Switch>
            </>
          )}
        </Suspense>
      </div>
    </AppConfiguration>
  );
};

export default withAppInsights(withRouter(App));
